import PropTypes from "prop-types";
import {
  Layout,
  Badge,
  Input as AntInput,
  Spin,
} from "@/config/antd";
import { MenuOutlined, SearchOutlined } from "@/config/antd";
import { SlideOutMenu } from "./Slide_Out_Menu";
import WebIcon from "@/components/common/Web_Icons";
import Avatar from "@/components/common/Avatar";
import "@/assets/styles/custom_hamburger_menu.css";
import Logo from "@/components/common/Logo";
import { useSelector, useDispatch } from "react-redux";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";
import { useState, useEffect, useRef } from "react";
import Button from "@/components/common/Button";
import { Link } from "react-router-dom";
import { getFirstName } from "@/utils/nameUtils";
import {
  selectFetchedTickets,
} from "@/store/redux/slices/support/support_slice";
import SupportChatDrawer from "@/components/layout/Header/support/SupportChatDrawer";
import { getRootTicketCount } from "@/utils/supportUtils";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { fetchSubEventsByOrg } from "@/store/redux/slices/event/sub_event_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { getSearchConfigForEntity } from "@/config/searchConfig";

const { Header: AntHeader } = Layout;

export const Header = ({
  menuVisible = false,
  setMenuVisible = () => console.warn("setMenuVisible function not provided"),
  onMenuClick = () => console.warn("onMenuClick function not provided"),
  navigate = () => console.warn("navigate function not provided"),
}) => {
  const dispatch = useDispatch();
  const userProfile = useSelector(selectUserProfile);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const supportTickets = useSelector(selectFetchedTickets);
  const organizationId = getOrganizationId();
  // Controls whether the suggestions dropdown is visible.
  // We close it on suggestion click, Enter, "View all", and clicks outside.
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchContainerRef = useRef(null);

  // Global search hook drives suggestions and full results.
  // Keeping this logic here allows the Header to remain the single
  // source of truth for query state while delegating heavy lifting
  // (debouncing, error handling) to the hook.
  const {
    query: globalSearchQuery,
    setQuery: setGlobalSearchQuery,
    suggestions,
    loadingSuggestions,
    performFullSearch,
  } = useGlobalSearch({ organizationId });

  const handleNotificationClick = () => {
    setNotificationVisible(true);
  };

  const handleSettingsNavigate = () => {
    navigate("settings");
  };

  const userRole = userProfile?.role;
  const firstName = getFirstName(userProfile?.fullname);
  const openTicketCount = getRootTicketCount(supportTickets);

  // Close the dropdown when the user clicks anywhere outside the search container.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleGlobalSearchChange = (e) => {
    setGlobalSearchQuery(e.target.value);
    setDropdownOpen(true);
  };

  const handleGlobalSearchEnter = async (e) => {
    e.preventDefault();
    const value = e.target.value;
    setDropdownOpen(false);
    await performFullSearch(value);
    if (value?.trim()) {
      navigate(`/dashboard/search?q=${encodeURIComponent(value.trim())}`);
    }
  };

  const handleSuggestionClick = async (item) => {
    const { entity, id } = item || {};
    const config = getSearchConfigForEntity(entity);
    if (!config || !id) return;

    setDropdownOpen(false);
    setGlobalSearchQuery("");

    let basePath = config.routeFromItem?.(item);

    if (!basePath && entity === "sub-events") {
      try {
        const res = await dispatch(
          fetchSubEventsByOrg({
            key: "_id",
            value: id,
          })
        ).unwrap();

        const subEvent = Array.isArray(res) ? res[0] : res;
        const eventId =
          subEvent?.eventID?._id ||
          subEvent?.eventID ||
          subEvent?.eventId?._id ||
          subEvent?.eventId;

        if (eventId) {
          basePath = `/dashboard/events/${eventId}/schedule/${id}`;
        }
      } catch (err) {
        console.error("Failed to resolve sub-event parent event:", err);
      }
    }

    if (!basePath && config.route) {
      basePath = config.route(id);
    }

    if (!basePath) return;

    const separator = basePath.includes("?") ? "&" : "?";
    navigate(`${basePath}${separator}highlight=true`);
  };

  const renderSearchDropdown = () => {
    const hasSuggestions = suggestions && suggestions.length > 0;
    const hasQuery = !!globalSearchQuery?.trim();

    if (!dropdownOpen || (!hasQuery && !loadingSuggestions)) return null;

    return (
      <div className="absolute top-full mt-2 left-0 z-50 w-[420px] max-w-[90vw] bg-white rounded-xl shadow-lg p-3 preserve-text-black">
        {loadingSuggestions && (
          <div className="flex items-center justify-center py-4">
            <Spin size="small" />
          </div>
        )}

        {!loadingSuggestions && !hasSuggestions && (
          <div className="py-3 text-small text-gray-500">
            No results found for &quot;{globalSearchQuery}&quot;
          </div>
        )}

        {!loadingSuggestions &&
          hasSuggestions &&
          suggestions.map((item) => {
            const { entity, label, id } = item;
            const config = getSearchConfigForEntity(entity);
            if (!config) return null;

            const IconSrc = config.icon;

            return (
              <button
                key={`${entity}-${id}`}
                type="button"
                onClick={() => handleSuggestionClick(item)}
                className="w-full flex items-center gap-3 px-2 py-2 hover:bg-secondary rounded-lg text-left"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-secondary flex-shrink-0">
                  {IconSrc && (
                    <img
                      src={IconSrc}
                      alt={config.label}
                      className="w-5 h-5 object-contain"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-small truncate">
                      {label}
                    </span>
                    <span className="text-xs text-gray-400 uppercase">
                      {config.label}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}

        {hasSuggestions && !loadingSuggestions && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              Showing top {suggestions.length} matches
            </span>
            {/* "See full results" navigates to /dashboard/search where results are
                grouped by entity type (Campaigns, Media, Tickets, etc.) with
                pagination — unlike the dropdown which only shows quick suggestions. */}
            <button
              type="button"
              className="text-primary text-small font-medium"
              onClick={async () => {
                const value = globalSearchQuery?.trim();
                if (!value) return;
                setDropdownOpen(false);
                await performFullSearch(value);
                navigate(`/dashboard/search?q=${encodeURIComponent(value)}`);
              }}
            >
              See full results →
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <AntHeader
      className="top-0 z-40 flex items-center justify-between border-b lg:sticky border-b-dark-500 transition-colors duration-200"
      style={{
        backgroundColor: "var(--header-bg)",
        width: "100%",
        maxWidth: "100%",
        margin: 0,
        padding: 0,
      }}>
      <div className="header-content w-full">
        {/* Mobile header layout */}
        <div className="lg:hidden flex items-center justify-between w-full px-1 h-full">
          {/* Group hamburger and logo together */}
          <div className="flex items-center space-x-1">
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMenuVisible(true)}
              className="p-0 menu-button text-primary"
              aria-label="Menu"
            />
            <Logo name="CMSLogo" size="large" className="cursor-pointer mt-2" />
          </div>

          {/* Avatar on the right */}
          <Avatar
            src={userProfile.profileImage || undefined}
            onClick={handleSettingsNavigate}
            size={40}
            className="cursor-pointer"
          />
        </div>

        {/* Desktop header layout with grid */}
        <div className="hidden lg:grid lg:grid-cols-5 w-full h-full">
          {/* Logo area - aligns with sidebar */}
          <div className="flex items-center h-full px-6">
            <Link to="/dashboard" className="flex items-center h-full">
              <Logo name="CMSLogo" size="large" className="cursor-pointer" />
            </Link>
          </div>

          {/* Content area - spans 4 columns like the dashboard content */}
          <div className="col-span-4 flex items-center justify-between pr-6">
            {/* Search input area - aligned with content */}
            <div className="flex items-center h-full relative" ref={searchContainerRef}>
              <AntInput
                placeholder="Search..."
                prefix={<SearchOutlined className="text-lighterGrey" />}
                className="text-black hover:bg-secondary focus:bg-secondary rounded-full bg-secondary"
                value={globalSearchQuery}
                onChange={(e) => {
                  handleGlobalSearchChange(e);
                  if (!e.target.value) setDropdownOpen(false);
                }}
                onFocus={() => {
                  if (globalSearchQuery?.trim()) setDropdownOpen(true);
                }}
                onPressEnter={handleGlobalSearchEnter}
                allowClear
                style={{
                  width: "716px",
                  height: "38px",
                  maxWidth: "100%",
                }}
              />
              {renderSearchDropdown()}
            </div>

            {/* User info and notifications */}
            <div className="flex items-center gap-4 ml-auto">
              <div className="relative bg-secondary rounded-full w-20 h-10 ">
                <div className="absolute"></div>
                <Badge
                  count={openTicketCount}
                  overflowCount={99}
                  offset={[-20, 15]}
                  className="preserve-badge-color">
                  <div className="relative translate-y-[-10px]">
                    <WebIcon
                      icon="NotificationBing"
                      size="large"
                      className="cursor-pointer w-16 h-16"
                      onClick={handleNotificationClick}
                    />
                  </div>
                </Badge>
              </div>

              <div className="flex items-center gap-3 cursor-pointer">
                <Avatar
                  src={userProfile.profileImage || undefined}
                  onClick={handleSettingsNavigate}
                  size={40}
                  className="cursor-pointer"
                />
                <div className="flex flex-col">
                  <span className="font-semibold text-small">{firstName}</span>
                  <span className="text-small-xs text-gray-500 whitespace-nowrap">
                    {userRole}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SlideOutMenu component */}
      <SlideOutMenu
        visible={menuVisible}
        onClose={() => setMenuVisible(false)}
        onMenuClick={onMenuClick}
      />

      <SupportChatDrawer
        open={notificationVisible}
        onClose={() => setNotificationVisible(false)}
        navigate={navigate}
      />
    </AntHeader>
  );
};

Header.propTypes = {
  menuVisible: PropTypes.bool,
  setMenuVisible: PropTypes.func,
  onMenuClick: PropTypes.func,
  navigate: PropTypes.func,
};
